import React from 'react';
import { Globe, Award, Users, TrendingUp, Check, ArrowRight, GraduationCap } from 'lucide-react';
import { bricsCountries, partnerUniversities, studentTestimonials, admissionProcess, scholarshipStats } from '../utils/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';

export default function Partners() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#0039A6] to-[#007A5E] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FCD116] rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <Globe className="text-[#FCD116]" size={20} />
              <span className="font-medium">Réseau International BRICS+</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Nos Universités Partenaires
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Accédez aux meilleures universités en Russie, Inde, Chine, Singapore et dans les pays BRICS avec notre accompagnement complet
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-[#FCD116]">{scholarshipStats.studentsAbroad}</div>
                <div className="text-sm text-white/80">Étudiants à l'étranger</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-[#FCD116]">{scholarshipStats.successRate}%</div>
                <div className="text-sm text-white/80">Taux de réussite</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-[#FCD116]">{scholarshipStats.scholarshipsObtained}</div>
                <div className="text-sm text-white/80">Bourses obtenues</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold text-[#FCD116]">{scholarshipStats.countries}</div>
                <div className="text-sm text-white/80">Pays partenaires</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRICS Countries Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Destinations BRICS+
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Étudiez dans les économies les plus dynamiques du monde avec des opportunités de bourses exceptionnelles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {bricsCountries.map((country) => (
              <Card key={country.id} className="hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4" style={{ borderTopColor: country.color }}>
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-6xl">{country.flag}</span>
                    <Badge style={{ backgroundColor: country.color }} className="text-white">
                      {country.universities} universités
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl">{country.name}</CardTitle>
                  <p className="text-sm text-gray-500">{country.nameEn}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{country.description}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Users size={16} className="text-[#FCD116]" />
                    <span>{country.students} étudiants camerounais</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Partner - RUDN Section */}
      <section className="py-20 bg-gradient-to-br from-[#D52B1E] to-[#b82418] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-[#FCD116] text-gray-900 hover:bg-[#FCD116] text-lg px-6 py-2 mb-4">
                🏆 Partenaire Principal
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Université Russe de l'Amitié des Peuples (RUDN)
              </h2>
              <p className="text-xl text-white/90">
                Российский Университет Дружбы Народов
              </p>
            </div>

            <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl">
                        🇷🇺
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">Moscou, Russie</p>
                        <Badge className="bg-[#FCD116] text-gray-900 hover:bg-[#FCD116] mt-1">
                          Top 400 mondial
                        </Badge>
                      </div>
                    </div>

                    <p className="text-white/90 text-lg mb-6 leading-relaxed">
                      Université internationale de prestige accueillant plus de <strong>30 000 étudiants de 160 pays</strong>. 
                      Leader en formation médicale, ingénierie et sciences humaines.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-[#FCD116]" />
                        <span className="text-white">Formation 100% gratuite avec bourse</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-[#FCD116]" />
                        <span className="text-white">Programme d'année préparatoire inclus</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-[#FCD116]" />
                        <span className="text-white">Diplôme reconnu mondialement</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-[#FCD116]" />
                        <span className="text-white">Campus moderne et résidences étudiantes</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold mb-4 text-white">Spécialités Disponibles:</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {["Médecine Générale", "Pharmacie", "Stomatologie", "Ingénierie Civile", "Architecture", "Informatique", "Économie", "Relations Internationales", "Droit", "Philologie", "Écologie", "Agronomie"].map((specialty, idx) => (
                        <div key={idx} className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg text-sm font-medium text-white border border-white/20 hover:bg-white/20 transition-colors">
                          {specialty}
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                      <h4 className="font-bold text-lg mb-3 text-white">🎓 Statistiques RUDN</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-3xl font-bold text-[#FCD116]">160+</div>
                          <div className="text-sm text-white/70">Pays représentés</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-[#FCD116]">30,000+</div>
                          <div className="text-sm text-white/70">Étudiants</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-[#FCD116]">95+</div>
                          <div className="text-sm text-white/70">Camerounais</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-[#FCD116]">100%</div>
                          <div className="text-sm text-white/70">Bourses</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <Link to="/contact">
                    <Button size="lg" className="bg-white text-[#D52B1E] hover:bg-gray-100 px-10 py-6 text-lg font-bold shadow-xl">
                      Candidater pour RUDN
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Partner - Acharya University Section */}
      <section className="py-20 bg-gradient-to-br from-[#FF9933] to-[#FF6B00] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-white text-[#FF9933] hover:bg-white text-lg px-6 py-2 mb-4">
                🏆 Partenaire Premier Rang - Inde
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Acharya University
              </h2>
              <p className="text-xl text-white/90">
                Université de Bangalore - Hub Technologique de l'Inde
              </p>
            </div>

            <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl">
                        🇮🇳
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">Bangalore, Karnataka, Inde</p>
                        <Badge className="bg-white text-[#FF9933] hover:bg-white mt-1">
                          Top université privée
                        </Badge>
                      </div>
                    </div>

                    <p className="text-white/90 text-lg mb-6 leading-relaxed">
                      Université moderne située dans la <strong>Silicon Valley de l'Inde</strong>. 
                      Campus high-tech de 120 acres avec infrastructures de classe mondiale et forte orientation internationale.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-white" />
                        <span className="text-white">Bourses mérite disponibles</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-white" />
                        <span className="text-white">Campus moderne avec laboratoires high-tech</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-white" />
                        <span className="text-white">Programmes en anglais</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-white" />
                        <span className="text-white">Stages dans les entreprises tech de Bangalore</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check size={20} className="text-white" />
                        <span className="text-white">Résidences étudiantes sur campus</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold mb-4 text-white">Programmes & Spécialités:</h3>
                    <div className="space-y-4">
                      <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                        <h4 className="font-bold text-white mb-2">🔧 Ingénierie & Technologie</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-white/90">• Computer Science</span>
                          <span className="text-white/90">• Electronics & Comm.</span>
                          <span className="text-white/90">• Mechanical Eng.</span>
                          <span className="text-white/90">• Civil Engineering</span>
                          <span className="text-white/90">• AI & Machine Learning</span>
                          <span className="text-white/90">• Data Science</span>
                        </div>
                      </div>

                      <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                        <h4 className="font-bold text-white mb-2">💼 Business & Management</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-white/90">• MBA</span>
                          <span className="text-white/90">• BBA</span>
                          <span className="text-white/90">• Finance</span>
                          <span className="text-white/90">• Marketing</span>
                          <span className="text-white/90">• International Business</span>
                          <span className="text-white/90">• Entrepreneurship</span>
                        </div>
                      </div>

                      <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                        <h4 className="font-bold text-white mb-2">🎨 Design & Architecture</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-white/90">• Architecture</span>
                          <span className="text-white/90">• Interior Design</span>
                          <span className="text-white/90">• Fashion Design</span>
                          <span className="text-white/90">• Product Design</span>
                        </div>
                      </div>

                      <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                        <h4 className="font-bold text-white mb-2">📊 Autres Programmes</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-white/90">• Pharmacy</span>
                          <span className="text-white/90">• Biotechnology</span>
                          <span className="text-white/90">• Media Studies</span>
                          <span className="text-white/90">• Psychology</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <h4 className="font-bold text-lg mb-4 text-white text-center">🎯 Points Forts Acharya</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white">120</div>
                        <div className="text-sm text-white/70">Acres de campus</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white">15,000+</div>
                        <div className="text-sm text-white/70">Étudiants</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white">50+</div>
                        <div className="text-sm text-white/70">Programmes</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white">95%</div>
                        <div className="text-sm text-white/70">Placement</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <Link to="/contact">
                    <Button size="lg" className="bg-white text-[#FF9933] hover:bg-gray-100 px-10 py-6 text-lg font-bold shadow-xl">
                      Candidater pour Acharya
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Universities Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Universités Partenaires Prestigieuses
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Des institutions de renommée mondiale avec lesquelles nous avons des accords de partenariat
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnerUniversities.filter(u => !u.featured).map((university) => (
              <Card key={university.id} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="text-[#0039A6]" size={24} />
                    {university.tuitionFree && (
                      <Badge className="bg-green-500 hover:bg-green-500">Gratuit</Badge>
                    )}
                    {university.scholarships && (
                      <Badge className="bg-[#FCD116] text-gray-900 hover:bg-[#FCD116]">Bourses</Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl mb-2">{university.name}</CardTitle>
                  <p className="text-sm text-gray-600">{university.city}, {university.country}</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Badge variant="outline" className="mb-2">{university.ranking}</Badge>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Spécialités:</p>
                    <div className="flex flex-wrap gap-2">
                      {university.specialties.slice(0, 3).map((specialty, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-700">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Student Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-[#0039A6] to-[#007A5E] text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Nos Étudiants à l'International
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Découvrez les parcours inspirants de nos étudiants actuellement en formation à l'étranger
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {studentTestimonials.map((student) => (
              <Card key={student.id} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all">
                <CardContent className="pt-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl flex-shrink-0">
                      {student.country === "Russie" ? "🇷🇺" : student.country === "Inde" ? "🇮🇳" : student.country === "Singapore" ? "🇸🇬" : "🇨🇳"}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-white mb-1">{student.name}</h3>
                      <p className="text-sm text-white/70">{student.program}</p>
                      <p className="text-sm text-[#FCD116]">{student.university}</p>
                      <p className="text-xs text-white/60 mt-1">{student.city}, {student.country}</p>
                    </div>
                    <Badge className="bg-[#FCD116] text-gray-900 hover:bg-[#FCD116]">
                      {student.currentStatus}
                    </Badge>
                  </div>
                  <p className="text-white/90 italic leading-relaxed">
                    "{student.quote}"
                  </p>
                  <p className="text-xs text-white/50 mt-3">Promotion {student.year}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Processus d'Accompagnement
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              De la consultation initiale jusqu'à votre départ, nous vous accompagnons à chaque étape
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {admissionProcess.map((step, index) => (
                <div key={step.step} className="relative">
                  <Card className="h-full hover:shadow-xl transition-shadow border-t-4 border-t-[#0039A6]">
                    <CardContent className="pt-8">
                      <div className="w-16 h-16 bg-[#0039A6] rounded-full flex items-center justify-center mb-4 mx-auto">
                        <span className="text-white text-2xl font-bold">{step.step}</span>
                      </div>
                      <h3 className="font-bold text-xl text-gray-900 mb-3 text-center">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 text-center">
                        {step.description}
                      </p>
                      <div className="flex items-center justify-center gap-2 text-sm text-[#007A5E]">
                        <TrendingUp size={16} />
                        <span className="font-medium">{step.duration}</span>
                      </div>
                    </CardContent>
                  </Card>
                  {index < admissionProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="text-[#FCD116]" size={32} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi Étudier avec nos Partenaires BRICS ?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                "Bourses gouvernementales jusqu'à 100%",
                "Universités de renommée mondiale (Top 100)",
                "Programmes en anglais ou en russe",
                "Coût de la vie abordable",
                "Diplômes reconnus internationalement",
                "Opportunités de carrière exceptionnelles",
                "Accompagnement personnalisé complet",
                "Réseau d'anciens étudiants établi"
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                  <div className="w-8 h-8 bg-[#007A5E] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check size={18} className="text-white" />
                  </div>
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0039A6] text-white">
        <div className="container mx-auto px-4 text-center">
          <Award size={64} className="mx-auto mb-6 text-[#FCD116]" />
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Prêt à Commencer Votre Parcours International ?
          </h2>
          <p className="text-xl mb-8 text-white/80 max-w-2xl mx-auto">
            Réservez une consultation gratuite avec nos conseillers d'orientation pour discuter de vos options
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#D52B1E] hover:bg-[#b82418] text-white px-10 py-6 text-lg shadow-2xl">
                Réserver une Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/courses">
              <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0039A6] px-10 py-6 text-lg">
                Voir nos Cours
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
